// v2/router/ProtectedRoute.tsx — Auth and permission gating for routes

import React from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from 'context/AuthContext';
import { Action, Role } from '../shared/types/domain';
import { usePermission } from '../shared/hooks/usePermission';

interface ProtectedRouteProps {
  /** Optional action required to access this route */
  requiredAction?: Action;
}

/**
 * Wraps child routes with authentication and optional permission checks.
 * - Unauthenticated users are redirected to /login with the current path preserved in state.
 * - Authenticated users missing a required permission are redirected to /v2/.
 */
export function ProtectedRoute({ requiredAction }: ProtectedRouteProps): React.ReactElement {
  const { isAuthenticated } = useAuth();
  const location = useLocation();

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  if (requiredAction) {
    return <PermissionGate action={requiredAction} />;
  }

  return <Outlet />;
}

/** Inner component that uses the usePermission hook (hooks can't be called conditionally) */
function PermissionGate({ action }: { action: Action }): React.ReactElement {
  const { userDetails } = useAuth();
  const role = (userDetails?.role as Role) ?? Role.SALES_USER;
  const isAllowed = usePermission(role, action, undefined, undefined);

  if (!isAllowed) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4 text-center p-4">
        <h2 className="text-2xl font-semibold text-foreground">Access Denied</h2>
        <p className="text-muted-foreground max-w-md">
          You don't have permission to access this page. Contact your administrator if you believe this is an error.
        </p>
      </div>
    );
  }

  return <Outlet />;
}
